import type {
  WidgetCoreDefinition,
  TeXMLContext,
  TeXMLElement,
  ValidationResult,
  TelnyxEvent,
  CallbackResult,
} from './types';
import { buildCallbackUrl } from './types';
import { getVoiceAttributes } from './voice';
import { interpolateVariables, resolveMessage } from './interpolate';
export const playAudioSources = ['catalog', 'custom_url'] as const;
export type PlayAudioSource = (typeof playAudioSources)[number];

export const greetingTypes = ['none', 'say', 'play'] as const;
export type GreetingType = (typeof greetingTypes)[number];

export type VoicemailConfig = {
  greetingType: GreetingType;
  greeting: string | Record<string, string>;
  source?: PlayAudioSource;
  audioFileId?: string;
  audioFileName?: string;
  audioUrl?: string;
  customUrl?: string;
  maxDuration: number;
  silenceTimeoutSeconds?: number;
  playBeep: boolean;
};

const DEFAULT_SILENCE_TIMEOUT_SECONDS = 5;

function resolveAudioUrl(config: VoicemailConfig): string {
  const source = config.source === 'custom_url' ? 'custom_url' : 'catalog';
  if (source === 'custom_url') {
    return (config.customUrl ?? config.audioUrl ?? '').trim();
  }
  return (config.audioUrl ?? '').trim();
}

function generateTeXML(config: VoicemailConfig, context: TeXMLContext): TeXMLElement[] {
  const elements: TeXMLElement[] = [];
  const languageCode = context.variables._language;

  if (config.greetingType === 'say') {
    const message = interpolateVariables(
      resolveMessage(config.greeting, languageCode, context.languageConfig?.defaultLanguage),
      context.variables,
      context.speechFormats,
    );

    if (message) {
      elements.push({
        tag: 'Say',
        attributes: getVoiceAttributes(
          context.voiceConfig,
          languageCode,
          context.languageConfig,
          context.elevenLabsApiKeyRef,
        ),
        content: message,
      });
    }
  } else if (config.greetingType === 'play') {
    const audioUrl = resolveAudioUrl(config);
    if (audioUrl) {
      elements.push({
        tag: 'Play',
        content: audioUrl,
      });
    }
  }

  elements.push({
    tag: 'Record',
    attributes: {
      action: buildCallbackUrl(context),
      method: 'POST',
      maxLength: config.maxDuration.toString(),
      playBeep: config.playBeep ? 'true' : 'false',
      timeout: (config.silenceTimeoutSeconds ?? DEFAULT_SILENCE_TIMEOUT_SECONDS).toString(),
      finishOnKey: '#',
    },
  });

  return elements;
}

export function voicemailCallbackHandler(
  event: TelnyxEvent,
  _config: VoicemailConfig,
  _variables: Record<string, string>,
): CallbackResult {
  const recordingUrl = event.RecordingUrl;
  const duration = parseInt(event.RecordingDuration || '0', 10);

  if (!recordingUrl || duration === 0) {
    return { nextHandleId: 'no-message' };
  }

  return {
    nextHandleId: 'recorded',
    variables: {
      _voicemailRecordingUrl: recordingUrl,
      _voicemailDuration: String(duration),
    },
  };
}

function validateConfig(config: VoicemailConfig): ValidationResult {
  const errors: string[] = [];

  if (config.greetingType === 'say') {
    const hasMessage =
      typeof config.greeting === 'string'
        ? config.greeting.trim().length > 0
        : Object.values(config.greeting).some((v) => v.trim().length > 0);

    if (!hasMessage) {
      errors.push('Greeting message is required when greeting type is Say');
    }
  }

  if (config.greetingType === 'play') {
    const audioUrl = resolveAudioUrl(config);
    if (!audioUrl) {
      errors.push('Audio is required when greeting type is Play');
    }
  }

  if (config.maxDuration < 1 || config.maxDuration > 600) {
    errors.push('Max duration must be between 1 and 600 seconds');
  }

  if (
    config.silenceTimeoutSeconds !== undefined &&
    (config.silenceTimeoutSeconds < 1 || config.silenceTimeoutSeconds > 30)
  ) {
    errors.push('Silence timeout must be between 1 and 30 seconds');
  }

  return { valid: errors.length === 0, errors };
}

export const voicemailWidgetCore: WidgetCoreDefinition<VoicemailConfig> = {
  id: 'voicemail',
  version: 1,
  name: 'Voicemail',
  description: 'Record a voicemail message from the caller',
  category: 'voice',
  tier: 'standard',

  defaultConfig: {
    greetingType: 'say',
    greeting: 'Please leave a message after the beep. Press pound when finished.',
    source: 'catalog',
    audioFileId: '',
    audioFileName: '',
    audioUrl: '',
    customUrl: '',
    maxDuration: 120,
    silenceTimeoutSeconds: DEFAULT_SILENCE_TIMEOUT_SECONDS,
    playBeep: true,
  },

  variables: {
    outputs: [{ name: 'voicemailDuration', description: 'Duration of the voicemail in seconds', type: 'string' }],
  },

  texml: {
    generate: generateTeXML,
    requiresCallback: true,
    callbackHandler: voicemailCallbackHandler,
  },

  validation: {
    validateConfig,
  },
};
